import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "antd";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import * as _ from "lodash";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";

import { useMachine } from "@/_zustand/machine";
import { homeStore } from "@/_zustand/home";
import { useFavoriteStore } from "@/_zustand/favorite";
import { useListaEmpresas } from "@/hooks/useEmpresas";
import { useCompanyFlow } from "@/hooks/useCompanyFlow";
import Logo from "@/assets/imgs/logo.png";

import { Swiper, SwiperSlide } from "swiper/react";
// Import Swiper styles
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/pagination";

// import required modules
import { Pagination } from "swiper/modules";

import "./styles.css";

export const Tiendas = () => {
  const navigate = useNavigate();
  const { getCompanyRoute } = useCompanyFlow();
  const queryClient = useQueryClient();
  const { clearMachine } = useMachine();
  const { setStore, setComuna, setMaquina } = homeStore();
  const isMutating = useIsMutating({ mutationKey: ["listaEmpresasMutate"] });
  const cacheEmpresas = queryClient.getQueryData(["empresas"]);
  const query = useListaEmpresas();
  const { viewFilter, favorites, setViewFilter } = useFavoriteStore();

  // El cache que llenan otros componentes hermanos (ej. Categories.jsx) tiene
  // prioridad; el fetch propio de este componente es solo un fallback para
  // cuando ese cache nunca se llenó.
  const tiendas =
    cacheEmpresas ?? (Array.isArray(query.data) ? query.data : null);

  const tiendasAMostrar =
    viewFilter === "favorites" ? _.uniqBy(favorites, "empresa_id") : tiendas;

  const isLoading = isMutating > 0 || (!cacheEmpresas && query.isLoading);
  const hasError = !cacheEmpresas && !Array.isArray(query.data) && query.isError;

  const handleClickTienda = async (id) => {
    setStore(id);
    clearMachine();
    setComuna(0);
    setMaquina(0);
    return navigate(getCompanyRoute(`stores/${id}`), { replace: true });
  };

  const handleVerTodasLasTiendas = () => {
    setViewFilter("all");
  };

  const handleImageError = (event) => {
    event.currentTarget.src = Logo;
  };

  const TiendasComponent = () => {
    if (_.size(tiendasAMostrar) > 0) {
      return (
        <>
          <Swiper
            slidesPerView={3}
            spaceBetween={20}
            grabCursor={true}
            pagination={{
              clickable: true,
            }}
            modules={[Pagination]}
            className="mySwiper"
            breakpoints={{
              340: {
                slidesPerView: 3,
                spaceBetween: 10,
              },
              768: {
                slidesPerView: 5,
                spaceBetween: 20,
              },
              1024: {
                slidesPerView: 8,
                spaceBetween: 20,
              },
            }}
          >
            {tiendasAMostrar.map((item) => (
              <SwiperSlide key={item.empresa_id}>
                <img
                  className="shadow p-2 img-fluid"
                  onClick={() => handleClickTienda(item.empresa_id)}
                  src={item.logo_empresa || Logo}
                  onError={handleImageError}
                  alt={item.empresa_nombre}
                  style={{
                    width: "100px", // Ancho predeterminado
                    height: "100px", // Alto predeterminado
                    objectFit: "scale-down", // Ajusta la imagen para cubrir el área establecida
                    borderRadius: "50%",
                  }}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </>
      );
    }

    if (viewFilter === "favorites") {
      return (
        <div className="text-center">
          <h4>Aún no tienes tiendas favoritas.</h4>
          <button
            type="button"
            className="btn btn-link"
            onClick={handleVerTodasLasTiendas}
          >
            Ver todas las tiendas
          </button>
        </div>
      );
    }

    return <h4 className="text-center">No se encontraron tiendas</h4>;
  };

  if (hasError) {
    return (
      <div className="text-center py-3">
        <p>No pudimos cargar las tiendas.</p>
        <button
          type="button"
          className="btn btn-link"
          onClick={() => query.refetch()}
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (!tiendasAMostrar && isLoading) {
    return <div>Cargando tiendas...</div>;
  }

  return (
    <section className="container-fluid" style={{ paddingTop: "4%" }}>
      <div id="tiendas" className="container py-3">
        <h2 className="text-left prompt-bold text-black title-20 my-3">
          { viewFilter == "all" ? "Tiendas" : "Mis tiendas "}
        </h2>
        <span style={{ textAlign: "right" }}>
          {tiendas && _.size(tiendas) > 0 ? (
            <Link
              to={getCompanyRoute("stores/0")}
              className="nav-link active mx-3"
              aria-current="page"
            >
              Ver todos
            </Link>
          ) : (
            <></>
          )}
        </span>

        <div className="col-12 justify-content-center align-items-center gap-3 fw-bold">
          <Skeleton loading={isLoading}>
            <TiendasComponent />
          </Skeleton>
        </div>
      </div>
    </section>
  );
};
